import { Palette } from '@mui/material';
import {
	abmStyles,
	aceStyles,
	atlanticStyles,
	citrinStyles,
	cityParkingStyles,
	diamondParkingStyles,
	drivnStyles,
	iconStyles,
	ipmStyles,
	lasVegasParkingStyles,
	legacyStyles,
	littlemanStyles,
	makeParkingPaymentStyles,
	metroOneStyles,
	nationalParkingStyles,
	oneParkingStyles,
	ovgStyles,
	parkchirpDarkStyles,
	parkchirpStyles,
	parkingAdvantageStyles,
	parkingCompanyOfAmericaStyles,
	parkingManagementCompanyStyles,
	parkingNwStyles,
	parkingSystemsOfAmericaStyles,
	parkwellStyles,
	parkXStyles,
	pcamStyles,
	pciStyles,
	peakStyles,
	pivotStyles,
	platinumStyles,
	premiumStyles,
	primeStyles,
	proparkStyles,
	raffleParkingStyles,
	rezyParkStyles,
	signatureParkingStyles,
	truparkStyles,
	unitedParkingSystemsStyles,
	universalStyles,
	vpneStyles,
} from '../constants';
import { OrganizationThemeEnum } from '../enums';
import { ThemeStyles } from '../types';

export function getOrgThemeName(org?: string) {
	try {
		if (!org) {
			return OrganizationThemeEnum.DEFAULT;
		}

		const normalizeOrg = org.toLocaleLowerCase();

		const themeName = Object.values(OrganizationThemeEnum).find((o) => o === normalizeOrg);

		return themeName || OrganizationThemeEnum.DEFAULT;
	} catch {
		return OrganizationThemeEnum.DEFAULT;
	}
}

export function getThemeOverrides(org?: string) {
	const orgTheme = getOrgThemeName(org);

	if (![OrganizationThemeEnum.DEFAULT, OrganizationThemeEnum.DEFAULT_DARK].includes(orgTheme)) {
		return {};
	}

	let overrides = {};

	try {
		const definition = localStorage?.getItem('parkchirp-overrides');
		if (definition) {
			overrides = JSON.parse(definition);
		}
	} catch {}

	if (orgTheme === OrganizationThemeEnum.DEFAULT_DARK) {
		const styleId = 'parkchirp-dark';
		let style = document.getElementById(styleId);

		if (!style) {
			style = document.createElement('style');
			style.id = styleId;

			style.textContent = `
				:root {
					color-scheme: dark;
				}

				html, body {
					color: white;
				}

				a.tabNavSelection {
					color: white !important;
				}

				a:not(:has(svg)) {
					color: #5cc1ff !important;
				}

				.editor-modal-content .editor-modal-header-text {
					color: white;
				}

				.pca__dashboard--searchbox {
					border: 1px solid #f8f8f828;
					background-color: #f8f8f810;
				}
			`;

			document.head.appendChild(style);
		}
	}

	switch (process.env.NEXT_ENVIRONMENT as string) {
		case 'DEVELOPMENT':
		case 'QA':
			return {
				headerGradient: {
					background: 'linear-gradient(90deg, #2a2f3c 0%, #00c94b 100%);',
				},
				...overrides,
			};
		case 'STG':
			return {
				headerGradient: {
					background: 'linear-gradient(90deg, #2a2f3c 0%, #00aeef 100%);',
				},
				...overrides,
			};
		default:
			return { ...overrides };
	}
}

export function getThemeStyles(org?: string) {
	let orgName = org;

	try {
		orgName = localStorage?.getItem('parkchirp-theme') ?? orgName;
	} catch {}

	const orgTheme = getOrgThemeName(orgName);

	const themeStyles: ThemeStyles = {
		[OrganizationThemeEnum.DEFAULT]: parkchirpStyles,
		[OrganizationThemeEnum.DEFAULT_DARK]: parkchirpDarkStyles,
		[OrganizationThemeEnum.ABM]: abmStyles,
		[OrganizationThemeEnum.ACE]: aceStyles,
		[OrganizationThemeEnum.ATLANTIC]: atlanticStyles,
		[OrganizationThemeEnum.CITRIN]: citrinStyles,
		[OrganizationThemeEnum.CITY_PARKING]: cityParkingStyles,
		[OrganizationThemeEnum.DIAMOND_PARKING]: diamondParkingStyles,
		[OrganizationThemeEnum.DRIVN]: drivnStyles,
		[OrganizationThemeEnum.ICON]: iconStyles,
		[OrganizationThemeEnum.IPM]: ipmStyles,
		[OrganizationThemeEnum.LAS_VEGAS_PARKING]: lasVegasParkingStyles,
		[OrganizationThemeEnum.LEGACY]: legacyStyles,
		[OrganizationThemeEnum.LITTLE_MAN_PARKING]: littlemanStyles,
		[OrganizationThemeEnum.MAKE_PARKING_PAYMENT]: makeParkingPaymentStyles,
		[OrganizationThemeEnum.METRO_ONE]: metroOneStyles,
		[OrganizationThemeEnum.NATIONAL_PARKING]: nationalParkingStyles,
		[OrganizationThemeEnum.ONE_PARKING]: oneParkingStyles,
		[OrganizationThemeEnum.OVG]: ovgStyles,
		[OrganizationThemeEnum.PARKING_ADVANTAGE]: parkingAdvantageStyles,
		[OrganizationThemeEnum.PARKING_COMPANY_OF_AMERICA]: parkingCompanyOfAmericaStyles,
		[OrganizationThemeEnum.PARKING_MANAGEMENT_COMPANY]: parkingManagementCompanyStyles,
		[OrganizationThemeEnum.PARKING_NW]: parkingNwStyles,
		[OrganizationThemeEnum.PARKING_SYSTEMS_OF_AMERICA]: parkingSystemsOfAmericaStyles,
		[OrganizationThemeEnum.PARKWELL]: parkwellStyles,
		[OrganizationThemeEnum.PARKX]: parkXStyles,
		[OrganizationThemeEnum.PCAM]: pcamStyles,
		[OrganizationThemeEnum.PCI]: pciStyles,
		[OrganizationThemeEnum.PEAK_PARKING]: peakStyles,
		[OrganizationThemeEnum.PIVOT_PARKING]: pivotStyles,
		[OrganizationThemeEnum.PLATINUM]: platinumStyles,
		[OrganizationThemeEnum.PREMIUM]: premiumStyles,
		[OrganizationThemeEnum.PRIME]: primeStyles,
		[OrganizationThemeEnum.PROPARK]: proparkStyles,
		[OrganizationThemeEnum.RAFFLE_PARKING]: raffleParkingStyles,
		[OrganizationThemeEnum.REZY_PARK]: rezyParkStyles,
		[OrganizationThemeEnum.SIGNATURE_PARKING]: signatureParkingStyles,
		[OrganizationThemeEnum.TRUPARK]: truparkStyles,
		[OrganizationThemeEnum.UNITED_PARKING_SYSTEMS]: unitedParkingSystemsStyles,
		[OrganizationThemeEnum.UNIVERSAL]: universalStyles,
		[OrganizationThemeEnum.VPNE]: vpneStyles,
	};

	const themeValues = themeStyles[orgTheme] || themeStyles[OrganizationThemeEnum.DEFAULT]!;
	const themeOverrides = getThemeOverrides(orgName);

	return {
		...themeValues,
		...themeOverrides,
	};
}

export const getOrgColorTheme = (orgName?: string, appName?: string) => {
	const {
		mode = 'light',
		density = 'standard',
		background = {},
		primaryColor,
		header,
		headerGradient = {},
		sidebar,
		button,
		icon = {},
		text,
		table,
		tab = {},
		dataGrid = {},
		dataGridToolbar = {},
		paper = {},
		title,
		logo,
		monthlyCheckoutLogo,
		monthlyCheckoutTextColor,
		platformName,
		showLargeMenuIconButton = false,
		typography,
		socials,
		orgName: readableOrgName,
		siteUrl,
		favicon,
	} = getThemeStyles(orgName) as any;
	const fontFamily =
		appName === 'MONTHLY_CHECKOUT'
			? ['FilsonSoft-Bold', 'FilsonSoft-Heavy', 'FilsonSoft-Light', 'sans-serif'].join(',')
			: typography?.fontFamily || 'inherit';

	return {
		header,
		sidebar,
		button,
		text,
		table,
		tab,
		title,
		logo: appName === 'MONTHLY_CHECKOUT' && monthlyCheckoutLogo ? monthlyCheckoutLogo : logo,
		monthlyCheckoutTextColor,
		platformName,
		socials,
		siteUrl,
		readableOrgName,
		favicon:
			appName === 'MONTHLY_CHECKOUT' && monthlyCheckoutLogo ? { src: monthlyCheckoutLogo.small.src } : favicon,
		palette: {
			mode,
			background,
			primary: {
				main: primaryColor || text.color,
			},
			secondary: {
				main: sidebar.backgroundColor,
			},
		} as Palette,
		components: {
			MuiInputLabel: {
				styleOverrides: {
					root: {
						fontFamily,
						color: text.color,
					},
				},
			},
			MuiCard: {
				styleOverrides: {
					root: {
						backgroundColor: '#f8f8f8',
						border: '1px solid rgba(224, 224, 224, 1)',
						boxShadow: 'initial',
						...paper,
					},
				},
			},
			MuiTableContainer: {
				styleOverrides: {
					root: {
						...table,
					},
				},
			},
			MuiAccordion: {
				styleOverrides: {
					root: {
						fontFamily,
					},
				},
			},
			MuiAccordionSummary: {
				styleOverrides: {
					root: {
						fontFamily,
					},
				},
			},
			MuiAccordionDetails: {
				styleOverrides: {
					root: {
						fontFamily,
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						fontFamily,
						color: text.color,
					},
				},
			},
			MuiSelect: {
				styleOverrides: {
					root: {
						fontFamily,
						color: text.color,
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					root: {
						'& .parker-detail .MuiDataGrid-columnHeaders, & .parker-detail .MuiDataGrid-columnHeader': {
							backgroundColor: table.headerBackgroundColor,
						},
						'& .parker-detail .MuiDataGrid-columnHeaderTitle': {
							color: table.headerTextColor,
						},
						'& .parker-detail .MuiDataGrid-columnSeparator': {
							display: 'none',
						},
						'& .parker-detail .MuiDataGrid-columnHeader--withLeftBorder': {
							borderLeftColor: table.headerBackgroundColor,
						},
						'& .parker-detail .MuiDataGrid-columnHeader--withRightBorder': {
							borderRightColor: table.headerBackgroundColor,
						},
					},
					paper,
				},
			},
			MuiSvgIcon: {
				styleOverrides: {
					root: {
						'.MuiDataGrid-panel &[data-testid="CloseIcon"]': {
							color: 'black',
						},
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						fontFamily,
						padding: '6px 8px',
						margin: '0px 5px 0px 0px',
						...button,
						'&.Mui-disabled': { ...button, opacity: 0.5 },
						'& .MuiCircularProgress-svg': {
							color: text.buttonLoadingColor || text.color,
						},
						'&:hover': {
							backgroundColor: button[':hover'].backgroundColor ?? button.backgroundColor,
							color: button[':hover'].color ?? button.color,
							borderColor: button[':hover'].borderColor,
						},
						'&.inputButtons': {
							backgroundColor: 'transparent',
						},
						'&.inputButtons:hover': {
							backgroundColor: 'inherit',
						},
						'&.transparent-button:hover': {
							boxShadow: 'none',
							backgroundColor: 'transparent',
						},
					},
					outlined: {
						backgroundColor: '#00000000',
						color: text.color,
						'&:hover': {
							color: text.color,
							backgroundColor: '#00000000',
						},
						'&.Mui-disabled': {
							color: text.color,
							backgroundColor: '#00000000',
							opacity: 0.5,
						},
					},
				},
			},
			MuiContainer: {
				styleOverrides: {
					root: {
						// Allows us to switch between large and small logos
						'& #pca__sidebar--large-logo': {
							display: 'none',
						},
						'&#pca__sidebar&:hover': {
							width: '210px',
							'& #pca__sidebar--large-logo': {
								display: 'block',
							},
							'& #pca__sidebar--small-logo': {
								display: 'none',
							},
						},
						'& #pca__sidebar-logo': {
							paddingTop: '10px',
						},
						'&#pca__sidebar': sidebar,
					},
				},
			},
			MuiHeaderContainer: {
				styleOverrides: {
					root: {
						display: 'flex',
						justifyContent: 'space-between',
						'&#pca__header': {
							...header,
							...headerGradient,
							height: '50px',
						},
						'&#pca__detail_header': {
							...header,
						},
						'& #pca__header-center--title': {
							paddingTop: '4px',
							paddingBottom: '2px',
						},
						'& #pca__header-center--title h1, h4, h2, p, .header-sign-out, .tenant-profile': {
							color: title?.color || text.color,
							textTransform: 'capitalize',
						},
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						'&.Mui-selected': {
							borderRadius: '4px',
							backgroundColor: tab.backgroundColor || sidebar.backgroundColor,
							color: tab.color || '#ffffff',
						},
					},
				},
			},
			MuiTypography: {
				styleOverrides: {
					root: {
						color: text.color,
						fontFamily,
						// fontWeight: 'normal',
						// fontSize: '1rem',
						// lineHeight: '1.5',
					},
				},
			},
			MuiTableCell: {
				styleOverrides: {
					root: {
						color: text.color,
						padding: '15px',
					},
				},
			},
			MuiTableHead: {
				styleOverrides: {
					root: {
						backgroundColor: table.backgroundColor ?? 'white',
						color: 'black',
					},
				},
			},
			MuiTableRow: {
				styleOverrides: {
					root: {
						'&.pca__table--header .MuiTableCell-root': {
							color: table.headerTextColor,
							backgroundColor: table.headerBackgroundColor,
						},
					},
				},
			},
			MuiCircularProgress: {
				styleOverrides: {
					root: {
						'&.history-balance-loading svg': {
							color: text.color,
						},
					},
					svg: {
						color: text.color,
					},
				},
			},
			MuiBackdrop: {
				styleOverrides: {
					root: {
						zIndex: 9,
					},
				},
			},
			MuiIconButton: {
				styleOverrides: {
					root: {
						color: orgName === 'parkchirp' || orgName === 'ParkX' ? 'black' : title?.color,
						'& .MuiIconButton-sizeSmall': {
							color: 'black !important',
						},
						'& .MuiIconButton-sizeMedium .MuiIconButton-edgeEnd': {
							color: 'black !important',
						},
						...icon,
					},
				},
			},
			MuiDataGrid: {
				defaultProps: {
					density,
				},
				styleOverrides: {
					root: {
						'& .MuiDataGrid-toolbarContainer': {
							backgroundColor: '#f8f8f8',
							padding: '6px',
							borderRadius: '3px 3px 0 0',
							borderBottom: '1px solid rgba(224, 224, 224, 1)',
							gap: '0px',
							...dataGridToolbar,
						},
						'& .MuiIconButton-root': {
							color: 'black',
							...icon,
						},
						marginTop: '10px',
						height: '88vh',
						'& .MuiDataGrid-filterFormDeleteIcon': {
							color: 'black !important',
						},
						...(showLargeMenuIconButton && {
							'& .MuiDataGrid-menuIconButton': {
								width: '100px',
								height: '50px',
								borderRadius: '50%',
							},
						}),
						'& .MuiDataGrid-columnHeaderTitleContainerContent .MuiButton-root': {
							padding: '3px 8px !important',
						},
						'& .MuiDataGrid-cell .MuiButton-root': {
							padding: '3px 8px !important',
						},
						...dataGrid,
					},
				},
			},
			MuiInputAdornment: {
				styleOverrides: {
					root: {
						fontFamily,
						color: text.color,
						marginRight: '12px',
					},
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						fontFamily,
					},
				},
			},
			MuiToggleButton: {
				styleOverrides: {
					root: {
						fontFamily,
					},
				},
			},
			MuiButtonBase: {
				styleOverrides: {
					root: {
						'&.MuiIconButton-sizeMedium': {
							color: 'black !important', // Specifically for medium-sized buttons
						},
						...icon,
					},
				},
			},
			MuiDatePicker: {
				styleOverrides: {
					root: {
						'& .MuiInputBase-input': {
							fontFamily,
							color: text.color,
						},
						'& .MuiInputLabel-root': {
							fontFamily,
							color: text.color,
						},
						'& .MuiPickersDay-root': {
							fontFamily,
						},
						'& .MuiPickersDay-today': {
							borderColor: sidebar.backgroundColor,
							'&.Mui-selected': {
								backgroundColor: button.backgroundColor,
								color: '#ffffff',
							},
						},
						'& .MuiPickersDay-selected': {
							backgroundColor: button.backgroundColor,
							color: '#ffffff',
							'&:hover': {
								backgroundColor: button.backgroundColor,
								opacity: 0.8,
							},
						},
						'& .MuiPickersCalendarHeader-switchViewButton': {
							fontFamily,
							color: text.color,
						},
					},
				},
			},
			MuiAvatar: {
				styleOverrides: {
					root: {
						'&.header-profile-avatar-icon': {
							color: title.color,
						},
					},
				},
			},
		},
	};
};

export const siteMap: Record<string, string> = {
	'national-parking': 'national parking',
	'platinum.parkchirp': 'platinum',
	'universal.parkchirp': 'universal',
	abmparking: 'abm',
	asgpark: 'atlantic',
	chirpqa: 'parkchirp',
	citrin: 'citrin',
	cityparkingonline: 'cityparking inc.',
	drivnparking: 'drivn parking',
	goparkwell: 'parkwell',
	iconparking: 'icon',
	ipmseattle: 'ipm',
	legacyparking: 'legacy',
	littlemanparking: 'little man parking',
	lvparking: 'las vegas parking',
	makeparkingpayment: 'make parking payment',
	'metro-one': 'metro one',
	oneparking: 'one parking',
	parkcheep: 'parkchirp',
	parkchirp: 'parkchirp',
	parkingadvantage: 'parking advantage',
	parkingconcepts: 'pci',
	parkingnw: 'parking nw',
	parkingsystemsofamerica: 'parking systems of america',
	parkingmgt: 'parking management company',
	parkovg: 'oak view group',
	parkpca: 'pcam',
	parkwithace: 'ace',
	parkwithdiamond: 'diamond parking',
	parkwithpca: 'parking company of america',
	parkwithprime: 'prime',
	raffleparking: 'raffle parking',
	parkxmanagement: 'parkx',
	peakparking: 'peak parking',
	pivotparking: 'pivot parking',
	platinumparking: 'platinum',
	propark: 'propark',
	rezypark: 'rezypark',
	signatureparkingtn: 'signature parking',
	truparkusa: 'trupark',
	unitedparkingsystem: 'united parking systems',
	universalparking: 'universal',
	vpne: 'vpne',
};
